import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCrown, FaCalendar, FaCreditCard } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';
import { MembershipBadge } from '../membership/MembershipBadge';
import { PaymentHistory } from '../payment/PaymentHistory';
import { UpgradeModal } from '../membership/UpgradeModal';
import { formatLocalDate } from '../../utils/formatters';
import { normalizeMembershipPayload } from '../../utils/membership';
import { ApiKeysPanel } from './ApiKeysPanel';
import { TradingAuthorityPanel } from './TradingAuthorityPanel';

export function AccountModal({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, refreshUserInfo } = useAuth();
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailTicketId, setEmailTicketId] = useState(null);
  const [emailStep, setEmailStep] = useState('idle'); // idle | sending | sent | verifying
  const [emailError, setEmailError] = useState(null);
  const [emailNotice, setEmailNotice] = useState(null);
  const [editingEmail, setEditingEmail] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!isAuthenticated) {
      onClose?.();
      return;
    }
    loadMembership();
  }, [open, isAuthenticated]);

  const loadMembership = async () => {
    setLoading(true);
    try {
      const data = await api.getMe();
      if (data.code === 'SUCCESS') {
        const normalizedMembership = normalizeMembershipPayload(data.data);
        setMembership(normalizedMembership);
        setEditingEmail(!normalizedMembership?.profile?.email);
      }
    } catch (err) {
      console.error('Failed to load membership:', err);
    }
    setLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('membership.forever');
    return formatLocalDate(
      dateStr,
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      },
      i18n.language
    );
  };

  const handleEmailSendCode = async () => {
    if (!emailValue) {
      setEmailError(t('auth.emailRequired'));
      return;
    }
    setEmailStep('sending');
    setEmailError(null);
    setEmailNotice(null);
    try {
      const data = await api.requestEmailBindCode(emailValue, i18n.language);
      if (data.code !== 'SUCCESS') {
        throw new Error(data.msg || t('account.emailBindFailed'));
      }
      setEmailTicketId(data.data.ticket_id);
      setEmailStep('sent');
      const masked = data.data.masked_email || emailValue;
      setEmailNotice(t('account.emailCodeSent', { email: masked }));
    } catch (err) {
      setEmailStep('idle');
      setEmailError(err.message || t('account.emailBindFailed'));
    }
  };

  const handleEmailBind = async () => {
    if (!emailTicketId) {
      setEmailError(t('account.emailBindFailed'));
      return;
    }
    if (!emailCode) {
      setEmailError(t('auth.codeRequired'));
      return;
    }
    setEmailStep('verifying');
    setEmailError(null);
    setEmailNotice(null);
    try {
      const data = await api.bindEmail(emailValue, emailTicketId, emailCode);
      if (data.code !== 'SUCCESS') {
        throw new Error(data.msg || t('account.emailBindFailed'));
      }
      const updated = await refreshUserInfo();
      if (updated) {
        setMembership(updated);
      } else {
        setMembership((prev) => ({
          ...(prev || {}),
          profile: { ...(prev?.profile || {}), email: data.data.email },
        }));
      }
      setEmailStep('idle');
      setEmailTicketId(null);
      setEmailCode('');
      setEmailValue('');
      setEditingEmail(false);
      setEmailNotice(t('account.emailBindSuccess'));
    } catch (err) {
      setEmailStep('sent');
      setEmailError(err.message || t('account.emailBindFailed'));
    }
  };

  const resetEmailForm = () => {
    setEmailValue('');
    setEmailCode('');
    setEmailTicketId(null);
    setEmailStep('idle');
    setEmailError(null);
    setEmailNotice(null);
  };

  const hasEmail = Boolean(membership?.profile?.email);
  const membershipLabel =
    membership?.is_expired
      ? t('membership.expired')
      : membership?.tier_info?.display_name ||
        t(`membership.${membership?.tier_name || 'free'}`);
  const membershipExpiryLabel = membership?.is_expired
    ? t('membership.expiredAt', { date: formatDate(membership?.expires_at) })
    : formatDate(membership?.expires_at);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
        <div className="w-full max-w-5xl max-h-[85vh] rounded-[28px] border border-white/10 bg-gradient-to-br from-surface/95 via-surface to-surface-highlight/70 shadow-[0_40px_120px_rgba(2,6,23,0.65)] ring-1 ring-white/5 flex flex-col">
          <div className="flex items-start justify-between gap-4 px-7 py-6 border-b border-white/5 bg-gradient-to-r from-white/5 via-transparent to-transparent shrink-0">
            <div>
              <div className="text-[11px] uppercase tracking-[0.35em] text-slate-500">
                {t('account.settingsLabel')}
              </div>
              <div className="mt-2 text-2xl font-semibold text-white">
                {t('account.settingsTitle')}
              </div>
            </div>
            <button
              className="rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
              onClick={onClose}
            >
              <FiX size={16} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-7 py-6 space-y-8">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-strong"></div>
              </div>
            ) : (
              <>
                <div className="bg-gradient-to-br from-surface-highlight/70 via-surface/90 to-surface-highlight/40 rounded-3xl p-6 border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.35)]">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-2xl bg-amber-500/10 border border-amber-400/20 flex items-center justify-center">
                        <FaCrown className="text-amber-300" />
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                          {t('account.membershipInfo')}
                        </div>
                        <div className="mt-1 text-lg font-semibold text-white">
                          {t('account.membershipInfo')}
                        </div>
                      </div>
                    </div>
                    <MembershipBadge
                      tier={membership?.tier_name || 'free'}
                      size="lg"
                      expired={Boolean(membership?.is_expired)}
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-gray-400 text-sm mb-1">{t('account.currentTier')}</div>
                      <div className={`text-lg font-medium ${membership?.is_expired ? 'text-rose-200' : 'text-white'}`}>
                        {membershipLabel}
                      </div>
                    </div>

                    <div>
                      <div className="text-gray-400 text-sm mb-1 flex items-center gap-1">
                        <FaCalendar size={12} />
                        {t('membership.expiresAt')}
                      </div>
                      <div className={`text-lg font-medium ${membership?.is_expired ? 'text-rose-300' : 'text-white'}`}>
                        {membershipExpiryLabel}
                      </div>
                    </div>
                  </div>



                  {membership?.tier_name !== 'pro' && (
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="mt-6 w-full bg-gradient-to-r from-accent-strong via-accent to-accent-soft text-white py-3 rounded-xl font-semibold hover:from-accent hover:to-accent-strong transition-colors shadow-[0_10px_30px_rgba(63,136,255,0.25)]"
                    >
                      {t('membership.upgrade')}
                    </button>
                  )}
                </div>

                <div className="bg-gradient-to-br from-surface-highlight/60 via-surface/90 to-surface-highlight/30 rounded-3xl p-6 border border-white/10 shadow-[0_18px_50px_rgba(0,0,0,0.3)]">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                        {t('account.backupEmailTitle')}
                      </div>
                      <h2 className="mt-1 text-lg font-semibold text-white">{t('account.backupEmailTitle')}</h2>
                      <p className="text-sm text-slate-400 mt-2">{t('account.backupEmailDesc')}</p>
                    </div>
                    {hasEmail && !editingEmail && (
                      <span className="px-2.5 py-1 text-[11px] rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 uppercase tracking-[0.2em]">
                        {t('account.emailBoundBadge')}
                      </span>
                    )}
                  </div>

                  {emailNotice && (
                    <div className="mb-3 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-2">
                      {emailNotice}
                    </div>
                  )}

                  {emailError && (
                    <div className="mb-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-2">
                      {emailError}
                    </div>
                  )}

                  {hasEmail && !editingEmail ? (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <div className="text-xs text-gray-400">{t('account.emailBoundLabel')}</div>
                        <div className="text-white text-sm">{membership?.profile?.email}</div>
                      </div>
                      <button
                        onClick={() => {
                          resetEmailForm();
                          setEditingEmail(true);
                        }}
                        className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white text-sm border border-white/10 transition-colors"
                      >
                        {t('account.changeEmail')}
                      </button>
                    </div>
                  ) : (
                    <div className="grid gap-4">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">{t('auth.emailLabel')}</label>
                        <input
                          value={emailValue}
                          onChange={(e) => setEmailValue(e.target.value)}
                          placeholder={t('auth.emailPlaceholder')}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                        />
                      </div>

                      {emailStep === 'sent' && (
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">{t('auth.codeLabel')}</label>
                          <input
                            value={emailCode}
                            onChange={(e) => setEmailCode(e.target.value)}
                            placeholder={t('auth.codePlaceholder')}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent/30"
                          />
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2">
                        {emailStep !== 'sent' ? (
                          <button
                            onClick={handleEmailSendCode}
                            disabled={emailStep === 'sending'}
                            className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white text-sm border border-white/10 transition-colors disabled:opacity-60"
                          >
                            {emailStep === 'sending' ? t('auth.sendingCode') : t('auth.sendCode')}
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={handleEmailBind}
                              disabled={emailStep === 'verifying'}
                              className="px-4 py-2 rounded-xl bg-gradient-to-r from-accent-strong to-accent text-white text-sm border border-accent/40 transition-colors disabled:opacity-60 shadow-[0_8px_20px_rgba(63,136,255,0.2)]"
                            >
                              {emailStep === 'verifying' ? t('auth.verifying') : t('auth.verifyCode')}
                            </button>
                            <button
                              onClick={handleEmailSendCode}
                              className="px-4 py-2 rounded-xl border border-white/10 text-slate-300 text-sm hover:text-white hover:bg-white/5 transition-colors"
                            >
                              {t('auth.resendCode')}
                            </button>
                          </>
                        )}

                        {hasEmail && (
                          <button
                            onClick={() => {
                              resetEmailForm();
                              setEditingEmail(false);
                            }}
                            className="px-4 py-2 rounded-xl border border-white/10 text-slate-400 text-sm hover:text-white hover:bg-white/5 transition-colors"
                          >
                            {t('common.cancel')}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <ApiKeysPanel active={open && !loading} />

                <TradingAuthorityPanel active={open && !loading} />

                <div className="bg-gradient-to-br from-surface-highlight/60 via-surface/90 to-surface-highlight/30 rounded-3xl p-6 border border-white/10 shadow-[0_18px_50px_rgba(0,0,0,0.3)]">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="h-10 w-10 rounded-2xl bg-sky-500/10 border border-sky-400/20 flex items-center justify-center">
                      <FaCreditCard className="text-sky-300" />
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500">
                        {t('payment.history')}
                      </div>
                      <h2 className="mt-1 text-lg font-semibold text-white">
                        {t('payment.history')}
                      </h2>
                    </div>
                  </div>
                  <PaymentHistory />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={membership?.tier_name || 'free'}
      />
    </>
  );
}
